import { CenteredStoryText } from '@/components/microstory/text';
import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { Scatterplot } from '@/components/scatterplot';
import { ScrollGraphic, ScrollRoot, ScrollStep, ScrollSteps } from '@/components/microstory/scroll';
import { countryIdToName } from '@/utils/country-id-to-name';
import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { t, Trans } from '@lingui/macro';
import { StoryText } from '@/components/microstory/text';
import { useLingui } from '@lingui/react';

type ClimateActionChapter3Props = {
  data: ClimateActionData['safer_when_coordinating_internationally'];
};

export function ClimateActionChapter3(props: ClimateActionChapter3Props) {
  const { data } = props;

  const { i18n } = useLingui();

  const scatterplotData = (Object.entries(data) as [CountryIsoCode, AgeGroupBreakdown][]).map(
    ([key, value]) => ({
      id: key,
      label: countryIdToName(key),
      ...value,
    })
  );

  const axisLabel = (generation: string) =>
    t({
      id: 'climate-action.scatterplot.axis-label',
      message:
        'More {generation} say their country would be safer if their governments worked with others',
      values: { generation },
    });

  return (
    <>
      <CenteredStoryText>
        <Trans id="climate-action.chapter-3.text-1">
          Surprisingly, we found no significant generational gap in these views: Older people are
          equally likely to believe the worst effects of climate change can be stopped and that bold
          government action is needed.
        </Trans>
      </CenteredStoryText>

      <CenteredStoryText>
        <Trans id="climate-action.chapter-3.text-2">
          One area where we did see a generational divide: whether countries should{' '}
          <AnnotatedType>cooperate</AnnotatedType> when it comes to tackling major global
          challenges.
        </Trans>
      </CenteredStoryText>

      <ScrollRoot>
        <ScrollGraphic>
          <Scatterplot
            data={scatterplotData}
            highlightedIds={['ID', 'US', 'NG', 'BD', 'ML']}
            idsAlignment={{ US: 'top', ID: 'right', NG: 'right', ML: 'bottom' }}
            getXValue={(d) => d.older}
            getYValue={(d) => d.younger}
            topAxisLabel={axisLabel(youngerGeneration(i18n))}
            bottomAxisLabel={axisLabel(olderGeneration(i18n))}
            hasLabels
          />
        </ScrollGraphic>

        <ScrollSteps>
          <ScrollStep>
            <StoryText>
              <Trans id="climate-action.chapter-3.text-3">
                Here young people are more <AnnotatedType>supportive</AnnotatedType> of national
                governments working together than on their own.
              </Trans>
            </StoryText>
          </ScrollStep>
          <ScrollStep>
            <StoryText>
              <Trans id="climate-action.chapter-3.text-4">
                This is especially true in <PrimaryAnnotatedType>Indonesia,</PrimaryAnnotatedType>{' '}
                <PrimaryAnnotatedType>the United States,</PrimaryAnnotatedType>{' '}
                <PrimaryAnnotatedType>Nigeria,</PrimaryAnnotatedType>{' '}
                <PrimaryAnnotatedType>Bangladesh</PrimaryAnnotatedType> and{' '}
                <PrimaryAnnotatedType>Mali.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
